import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, BookOpen, Layers, Feather, Trophy, DollarSign, Upload, Plus, Trash2, Pencil, X, Check, Crown } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import AdCMS from "./AdCMS";
import { PremiumPlans, PaymentsPanel } from "./PremiumCMS";
import SiteLayoutCMS from "./SiteLayoutCMS";
import SeoCMS from "./SeoCMS";
import TrackingCMS from "./TrackingCMS";

const TABS = ["Stats", "Books", "Publish Book", "Authors", "Content", "Premium", "Payments", "Users", "Ads", "Header & Footer", "SEO", "Tracking & Verification", "Settings"];

/* ---------- shared inputs ---------- */
const Field = (p) => <input {...p} className={`w-full bg-background border border-border rounded-xl px-4 py-2.5 outline-none focus:border-primary ${p.className || ""}`} />;
const Area = (p) => <textarea {...p} className={`w-full bg-background border border-border rounded-xl px-4 py-2.5 outline-none focus:border-primary ${p.className || ""}`} />;

function ImageUpload({ value, onChange, label = "Image", testid }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url); toast.success("Image uploaded");
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  return (
    <div>
      <p className="track-label text-xs text-muted-foreground mb-2">{label}</p>
      <div className="flex items-center gap-3">
        {value ? <img src={value} alt="" className="h-16 w-16 rounded-lg object-cover border border-border" /> : <div className="h-16 w-16 rounded-lg border border-dashed border-border grid place-items-center text-muted-foreground"><Upload className="h-5 w-5" /></div>}
        <div className="flex-1 space-y-2">
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm cursor-pointer hover:border-primary transition-colors">
            <Upload className="h-4 w-4" /> {busy ? "Uploading…" : "Upload from computer"}
            <input type="file" accept="image/*" onChange={upload} className="hidden" data-testid={testid} />
          </label>
          <Field value={value?.startsWith("data:") ? "" : (value || "")} onChange={(e) => onChange(e.target.value)} placeholder="…or paste image URL" />
        </div>
      </div>
    </div>
  );
}

function useAutosave(saveFn, delay = 900) {
  const [status, setStatus] = useState("idle");
  const timer = useRef(null);
  const latest = useRef(saveFn);
  latest.current = saveFn;
  const trigger = useCallback((payload) => {
    setStatus("saving");
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try { await latest.current(payload); setStatus("saved"); setTimeout(() => setStatus("idle"), 2500); }
      catch (e) { setStatus("error"); toast.error(apiError(e)); }
    }, delay);
  }, [delay]);
  useEffect(() => () => clearTimeout(timer.current), []);
  return { status, trigger };
}

function SaveBadge({ status }) {
  if (status === "saving") return <span className="text-xs text-muted-foreground animate-pulse">Saving…</span>;
  if (status === "saved") return <span className="text-xs text-primary inline-flex items-center gap-1"><Check className="h-3.5 w-3.5" />Saved</span>;
  if (status === "error") return <span className="text-xs text-destructive">Save failed</span>;
  return <span className="text-xs text-muted-foreground/60">Autosave on</span>;
}

function MultiSelect({ options, value, onChange, label }) {
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div>
      <p className="track-label text-xs text-muted-foreground mb-2">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button type="button" key={o.id} onClick={() => toggle(o.id)} data-testid={`ms-${label}-${o.slug || o.id}`}
            className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${value.includes(o.id) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/60"}`}>
            {o.name}
          </button>
        ))}
      </div>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-card border border-border rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5"><h3 className="text-xl font-display font-bold">{title}</h3><button onClick={onClose}><X className="h-5 w-5" /></button></div>
        {children}
      </div>
    </div>
  );
}

/* ---------- STATS ---------- */
function Stats() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const items = [
    ["Users", s.total_users, Users], ["Books", `${s.published_books}/${s.total_books}`, BookOpen],
    ["Chapters", s.total_chapters, Layers], ["Authors", s.total_authors, Feather],
    ["Genres", s.total_genres, Layers], ["Tropes", s.total_tropes, Layers],
    ["Series", s.total_series, Layers], ["Total Reads", s.total_reads, BookOpen],
    ["Premium Users", s.premium_users, Crown], ["Revenue", `$${s.revenue}`, DollarSign],
    ["Streak Users", s.active_streak_users, Trophy], ["Ad Unlocks", s.ad_unlocks, Upload],
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {items.map(([label, val, Icon]) => (
        <div key={label} className="rounded-2xl border border-border surface-2 p-5" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
          <div className="flex items-center justify-between"><p className="track-label text-xs text-muted-foreground">{label}</p><Icon className="h-4 w-4 text-primary" /></div>
          <p className="text-3xl font-display font-bold mt-2">{val}</p>
        </div>
      ))}
    </div>
  );
}

/* ---------- BOOKS ---------- */
function Books({ authors, genres, tropes, series }) {
  const [books, setBooks] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/admin/books").then((r) => setBooks(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, body) => { await api.patch(`/admin/books/${id}`, body); load(); };
  const del = async (id) => { if (!window.confirm("Delete this book and all its chapters?")) return; await api.delete(`/admin/books/${id}`); toast.success("Book deleted"); load(); };

  const openEdit = async (b) => { const { data } = await api.get(`/admin/books/${b.id}`); setEdit(data); };

  return (
    <div>
      <div className="space-y-2">
        {books.map((b) => (
          <div key={b.id} className="flex items-center gap-3 p-3 rounded-xl border border-border surface-2" data-testid={`admin-book-${b.slug}`}>
            <img src={b.cover} alt="" className="h-16 w-11 object-cover rounded" />
            <div className="min-w-0 flex-1">
              <p className="font-medium truncate">{b.title}</p>
              <p className="text-xs text-muted-foreground">{b.author?.name} · {b.chapters_count} ch · <span className="capitalize">{b.status}</span>{b.is_demo ? " · demo" : ""}</p>
            </div>
            <div className="hidden md:flex items-center gap-1">
              {[["trending", "Trend"], ["featured", "Feat"], ["premium", "Prem"], ["new_release", "New"], ["downloadable", "DL"]].map(([f, l]) => (
                <button key={f} onClick={() => patch(b.id, { [f]: !b[f] })} className={`text-[11px] px-2 py-1 rounded-md border ${b[f] ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{l}</button>
              ))}
            </div>
            <select value={b.status} onChange={(e) => patch(b.id, { status: e.target.value })} className="bg-background border border-border rounded-lg px-2 py-1 text-xs">
              {["published", "draft", "unpublished", "archived"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button onClick={() => openEdit(b)} data-testid={`edit-book-${b.slug}`} className="p-2 hover:text-primary"><Pencil className="h-4 w-4" /></button>
            <button onClick={() => del(b.id)} data-testid={`delete-book-${b.slug}`} className="p-2 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {!books.length && <p className="text-muted-foreground text-center py-10">No books yet. Use Publish Book to add one.</p>}
      </div>
      {edit && <BookEditModal book={edit} authors={authors} genres={genres} tropes={tropes} series={series} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function BookEditModal({ book, authors, genres, tropes, series, onClose, onSaved }) {
  const [f, setF] = useState({ ...book });
  const [chapters, setChapters] = useState(book.chapters || []);
  const fRef = useRef(f); fRef.current = f;
  const chRef = useRef(chapters); chRef.current = chapters;

  const persist = async () => {
    const d = fRef.current; const chs = chRef.current;
    await api.put(`/admin/books/${book.id}`, {
      title: d.title, author_id: d.author_id, cover: d.cover, hook: d.hook, description: d.description,
      genre_ids: d.genre_ids || [], trope_ids: d.trope_ids || [], series_id: d.series_id || null,
      book_number: d.book_number ? parseInt(d.book_number) : null, status: d.status,
      trending: d.trending, featured: d.featured, new_release: d.new_release, premium: d.premium,
      downloadable: d.downloadable,
      chapters: chs.map((c, i) => ({ number: i + 1, title: c.title, content: c.content, access_type: c.access_type || "auto", status: c.status || "published" })),
    });
  };
  const auto = useAutosave(async () => { await persist(); });
  const upd = (patch) => { setF({ ...f, ...patch }); auto.trigger(); };
  const updCh = (chs) => { setChapters(chs); auto.trigger(); };
  const save = async () => { try { await persist(); toast.success("Book saved"); onSaved(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <Modal title={<span className="flex items-center gap-3">Edit — {book.title} <SaveBadge status={auto.status} /></span>} onClose={() => { onSaved(); onClose(); }}>
      <div className="space-y-3">
        <Field value={f.title} onChange={(e) => upd({ title: e.target.value })} placeholder="Title" />
        <select value={f.author_id || ""} onChange={(e) => upd({ author_id: e.target.value })} className="w-full bg-background border border-border rounded-xl px-4 py-2.5">
          <option value="">Select author…</option>{authors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <Field value={f.hook} onChange={(e) => upd({ hook: e.target.value })} placeholder="Hook" />
        <Area rows={3} value={f.description} onChange={(e) => upd({ description: e.target.value })} placeholder="Description" />
        <MultiSelect label="Genres" options={genres} value={f.genre_ids || []} onChange={(v) => upd({ genre_ids: v })} />
        <MultiSelect label="Tropes" options={tropes} value={f.trope_ids || []} onChange={(v) => upd({ trope_ids: v })} />
        <ImageUpload label="Cover image" value={f.cover} onChange={(url) => upd({ cover: url })} testid="edit-cover-upload" />
        <div className="flex gap-3">
          <select value={f.series_id || ""} onChange={(e) => upd({ series_id: e.target.value || null })} className="flex-1 bg-background border border-border rounded-xl px-4 py-2.5">
            <option value="">Standalone</option>{series.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {f.series_id && <Field type="number" value={f.book_number || ""} onChange={(e) => upd({ book_number: e.target.value })} placeholder="Book #" className="w-28" />}
        </div>
        <div className="flex flex-wrap gap-4 text-sm">
          {["trending", "featured", "new_release", "premium", "downloadable"].map((k) => (
            <label key={k} className="flex items-center gap-2"><input type="checkbox" checked={!!f[k]} onChange={(e) => upd({ [k]: e.target.checked })} /> {k.replace("_", " ")}</label>
          ))}
        </div>
        <div>
          <p className="track-label text-xs text-muted-foreground mb-2">Chapters ({chapters.length})</p>
          <div className="space-y-2 max-h-52 overflow-y-auto">
            {chapters.map((c, i) => (
              <div key={i} className="space-y-1">
                <div className="flex gap-2 items-center">
                  <span className="text-xs w-6 text-muted-foreground">{i + 1}</span>
                  <Field value={c.title} onChange={(e) => { const n = [...chapters]; n[i] = { ...c, title: e.target.value }; updCh(n); }} className="flex-1" />
                  <button onClick={() => updCh(chapters.filter((_, j) => j !== i))} className="p-1.5 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                </div>
                <Area rows={2} value={c.content} onChange={(e) => { const n = [...chapters]; n[i] = { ...c, content: e.target.value }; updCh(n); }} placeholder="Chapter content…" className="text-sm" />
              </div>
            ))}
          </div>
          <button onClick={() => updCh([...chapters, { title: `Chapter ${chapters.length + 1}`, content: "New chapter content." }])} className="text-sm text-primary mt-2 flex items-center gap-1"><Plus className="h-4 w-4" /> Add chapter</button>
        </div>
        <button onClick={save} data-testid="save-book-btn" className="w-full px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold">Save Book Now</button>
      </div>
    </Modal>
  );
}

/* ---------- PUBLISH BOOK ---------- */
function PublishBook({ authors, genres, tropes, series, reload }) {
  const [f, setF] = useState({ title: "", author_id: "", hook: "", description: "", cover: "", status: "published", trending: false, featured: false, premium: false, downloadable: false, series_id: "", book_number: "" });
  const [genreIds, setGenreIds] = useState([]);
  const [tropeIds, setTropeIds] = useState([]);
  const [content, setContent] = useState("");
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setFileName(file.name);
    if (/\.docx?$/i.test(file.name)) {
      setBusy(true);
      try {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/admin/extract-text", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setContent(data.content);
        toast.success(`Loaded "${file.name}" — ready to publish`);
      } catch (err) { toast.error(apiError(err)); setFileName(""); }
      setBusy(false);
    } else {
      const r = new FileReader(); r.onload = () => setContent(r.result); r.readAsText(file);
    }
  };
  const publish = async () => {
    if (!f.title || !f.author_id || !content) return toast.error("Title, author and book file are required");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/books/publish-whole", {
        ...f, book_number: f.book_number ? parseInt(f.book_number) : null, series_id: f.series_id || null,
        genre_ids: genreIds, trope_ids: tropeIds, content,
      });
      toast.success(`Published "${data.book.title}" — ${data.chapters_detected} chapters detected`);
      setF({ title: "", author_id: "", hook: "", description: "", cover: "", status: "published", trending: false, featured: false, premium: false, downloadable: false, series_id: "", book_number: "" });
      setGenreIds([]); setTropeIds([]); setContent(""); setFileName(""); reload();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <div className="rounded-3xl border border-border surface-2 p-6 md:p-8">
      <h2 className="text-2xl font-display font-bold">Publish Whole Book in One Go</h2>
      <p className="text-sm text-muted-foreground mt-2">Upload a single <code>.txt</code>, <code>.md</code>, or Word <code>.docx</code> file containing the whole book. Chapters are auto-detected from <code>Chapter 1: Title</code>, markdown <code>## headings</code>, or <code>---</code> separators.</p>
      <div className="space-y-4 mt-6">
        <Field value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Book title" data-testid="publish-title" />
        <select value={f.author_id} onChange={(e) => setF({ ...f, author_id: e.target.value })} data-testid="publish-author" className="w-full bg-background border border-border rounded-xl px-4 py-2.5">
          <option value="">Select author…</option>{authors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <Field value={f.hook} onChange={(e) => setF({ ...f, hook: e.target.value })} placeholder="Hook (single sentence)" />
        <Area rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Description" />
        <ImageUpload label="Cover image" value={f.cover} onChange={(url) => setF({ ...f, cover: url })} testid="publish-cover-upload" />
        <div className="flex flex-wrap gap-4 items-center">
          <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="bg-background border border-border rounded-xl px-4 py-2.5">
            {["published", "draft", "unpublished"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {["trending", "featured", "premium", "downloadable"].map((k) => (
            <label key={k} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })} data-testid={`publish-${k}`} /> {k}</label>
          ))}
        </div>
        <MultiSelect label="Genres" options={genres} value={genreIds} onChange={setGenreIds} />
        <MultiSelect label="Tropes" options={tropes} value={tropeIds} onChange={setTropeIds} />
        <div className="flex gap-3">
          <select value={f.series_id} onChange={(e) => setF({ ...f, series_id: e.target.value })} className="flex-1 bg-background border border-border rounded-xl px-4 py-2.5">
            <option value="">Standalone</option>{series.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {f.series_id && <Field type="number" value={f.book_number} onChange={(e) => setF({ ...f, book_number: e.target.value })} placeholder="Book #" className="w-28" />}
        </div>
        <label className="block p-8 rounded-xl border border-dashed border-border text-center cursor-pointer hover:border-primary" data-testid="publish-file-drop">
          <Upload className="h-8 w-8 text-primary mx-auto" />
          <p className="font-semibold mt-2">{fileName || "Drop the whole-book file here or click to browse"}</p>
          <p className="text-xs text-muted-foreground mt-1">.txt, .md or Word .docx — split into chapters automatically</p>
          <input type="file" accept=".txt,.md,.docx,.doc" onChange={onFile} className="hidden" />
        </label>
        <button onClick={publish} disabled={busy} data-testid="publish-submit" className="w-full px-6 py-4 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2">
          <Plus className="h-5 w-5" /> {busy ? "Publishing…" : "Publish Whole Book"}
        </button>
      </div>
    </div>
  );
}

/* ---------- generic CRUD list for authors/genres/tropes/series ---------- */
function CrudSection({ title, endpoint, fields, listEndpoint, extra, reload }) {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const load = useCallback(() => api.get(listEndpoint).then((r) => setItems(r.data)), [listEndpoint]);
  useEffect(() => { load(); }, [load]);

  const save = async (data, id) => {
    try {
      if (id) await api.put(`${endpoint}/${id}`, data); else await api.post(endpoint, data);
      toast.success("Saved"); setModal(null); load(); reload && reload();
    } catch (e) { toast.error(apiError(e)); }
  };
  const autoSave = async (data, id) => { await api.put(`${endpoint}/${id}`, data); load(); reload && reload(); };
  const del = async (id) => { if (!window.confirm("Delete this item?")) return; try { await api.delete(`${endpoint}/${id}`); toast.success("Deleted"); load(); reload && reload(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-display font-bold">{title} ({items.length})</h3>
        <button onClick={() => setModal({})} data-testid={`add-${title.toLowerCase()}`} className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium"><Plus className="h-4 w-4" /> Add</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((it) => (
          <div key={it.id} className="flex items-center gap-3 p-3 rounded-xl border border-border surface-2">
            {(it.image || it.avatar || it.cover) && <img src={it.image || it.avatar || it.cover} alt="" className="h-12 w-12 rounded-lg object-cover" />}
            <div className="min-w-0 flex-1"><p className="font-medium truncate">{it.name}</p><p className="text-xs text-muted-foreground">{it.book_count != null ? `${it.book_count} books` : (it.followers != null ? `${it.followers} followers` : "")}</p></div>
            <button onClick={() => setModal(it)} className="p-2 hover:text-primary"><Pencil className="h-4 w-4" /></button>
            <button onClick={() => del(it.id)} className="p-2 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      {modal && <CrudModal title={title} fields={fields} extra={extra} item={modal} onClose={() => setModal(null)} onSave={save} onAutoSave={(data) => autoSave(data, modal.id)} />}
    </div>
  );
}

function CrudModal({ title, fields, extra, item, onClose, onSave, onAutoSave }) {
  const [f, setF] = useState({ ...item });
  const auto = useAutosave(async (data) => { await onAutoSave(data); });
  const upd = (patch) => { const n = { ...f, ...patch }; setF(n); if (item.id) auto.trigger(n); };
  return (
    <Modal title={<span className="flex items-center gap-3">{`${item.id ? "Edit" : "New"} ${title}`}{item.id && <SaveBadge status={auto.status} />}</span>} onClose={onClose}>
      <div className="space-y-3">
        {fields.map((fl) => fl.type === "area"
          ? <Area key={fl.k} rows={3} value={f[fl.k] || ""} onChange={(e) => upd({ [fl.k]: e.target.value })} placeholder={fl.label} />
          : fl.type === "image" ? <ImageUpload key={fl.k} label={fl.label} value={f[fl.k]} onChange={(url) => upd({ [fl.k]: url })} testid={`crud-upload-${fl.k}`} />
          : fl.type === "select" ? <select key={fl.k} value={f[fl.k] || ""} onChange={(e) => upd({ [fl.k]: e.target.value })} className="w-full bg-background border border-border rounded-xl px-4 py-2.5"><option value="">{fl.label}</option>{(extra?.[fl.opts] || []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select>
          : <Field key={fl.k} value={f[fl.k] || ""} onChange={(e) => upd({ [fl.k]: e.target.value })} placeholder={fl.label} data-testid={`crud-${fl.k}`} />)}
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active !== false} onChange={(e) => upd({ active: e.target.checked })} /> Active</label>
        {!item.id && <button onClick={() => onSave(f, item.id)} data-testid="crud-save" className="w-full px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold">Create</button>}
      </div>
    </Modal>
  );
}

/* ---------- USERS ---------- */
function UsersTab() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/users").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const act = async (id, action) => { await api.patch(`/admin/users/${id}`, { action, days: 30 }); toast.success("Updated"); load(); };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-muted-foreground track-label text-xs border-b border-border">
          {["Name", "Email", "Role", "Premium", "Status", "Actions"].map((h) => <th key={h} className="py-3 px-2">{h}</th>)}
        </tr></thead>
        <tbody>
          {rows.map((u) => (
            <tr key={u.id} className="border-b border-border/50" data-testid={`user-row-${u.email}`}>
              <td className="py-3 px-2">{u.name}</td><td className="px-2">{u.email}</td><td className="px-2 capitalize">{u.role}</td>
              <td className="px-2">{u.premium_active ? <span className="text-primary">Active</span> : "—"}</td>
              <td className="px-2 capitalize">{u.status}</td>
              <td className="px-2 flex gap-1 flex-wrap py-2">
                {u.premium_active ? <button onClick={() => act(u.id, "revoke_premium")} className="text-xs px-2 py-1 rounded border border-border">Revoke</button>
                  : <button onClick={() => act(u.id, "grant_premium")} className="text-xs px-2 py-1 rounded border border-border">Grant Premium</button>}
                {u.status === "active" ? <button onClick={() => act(u.id, "suspend")} className="text-xs px-2 py-1 rounded border border-border text-destructive">Suspend</button>
                  : <button onClick={() => act(u.id, "restore")} className="text-xs px-2 py-1 rounded border border-border">Restore</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- ADS / MONETIZATION ---------- */
function AdMediaUpload({ type, value, onChange }) {
  const [busy, setBusy] = useState(false);
  const up = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); onChange(data.url); toast.success("Uploaded"); }
    catch (e) { toast.error(apiError(e)); } setBusy(false);
  };
  return (
    <div>
      <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm cursor-pointer hover:border-primary">
        <Upload className="h-4 w-4" /> {busy ? "Uploading…" : `Upload ${type}`}
        <input type="file" accept={type === "video" ? "video/*" : "image/*"} onChange={up} className="hidden" data-testid="testad-upload" />
      </label>
      {value && (type === "video" ? <video src={value} controls className="mt-2 w-full max-h-40 rounded-lg border border-border" /> : <img src={value} alt="" className="mt-2 max-h-40 rounded-lg border border-border" />)}
    </div>
  );
}

function TestAds() {
  const [ads, setAds] = useState([]);
  const [f, setF] = useState({ title: "", brand_name: "Demo Brand", description: "", cta_text: "Learn More", destination_url: "", ad_type: "image", media_url: "" });
  const load = () => api.get("/admin/test-ads").then((r) => setAds(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!f.title || !f.media_url) return toast.error("Title and media required");
    await api.post("/admin/test-ads", f); toast.success("Test ad created & active"); setF({ ...f, title: "", media_url: "", description: "" }); load();
  };
  const toggle = async (a) => { await api.patch(`/admin/test-ads/${a.id}`, { status: a.status === "active" ? "paused" : "active" }); load(); };
  const del = async (a) => { await api.patch(`/admin/test-ads/${a.id}`, { delete: true }); load(); };
  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-border surface-2 p-6 space-y-3">
        <h3 className="text-lg font-display font-bold">Create Test Ad</h3>
        <p className="text-sm text-muted-foreground">Upload your own image/video to test the full chapter-unlock flow — no real advertiser needed.</p>
        <Field placeholder="Ad title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="testad-title" />
        <div className="flex gap-3"><Field placeholder="Brand name" value={f.brand_name} onChange={(e) => setF({ ...f, brand_name: e.target.value })} /><Field placeholder="CTA text" value={f.cta_text} onChange={(e) => setF({ ...f, cta_text: e.target.value })} /></div>
        <Field placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        <Field placeholder="Destination URL (optional)" value={f.destination_url} onChange={(e) => setF({ ...f, destination_url: e.target.value })} />
        <div className="flex gap-2">{["image", "video"].map((t) => <button key={t} onClick={() => setF({ ...f, ad_type: t, media_url: "" })} className={`px-4 py-2 rounded-full text-sm border ${f.ad_type === t ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{t}</button>)}</div>
        <AdMediaUpload type={f.ad_type} value={f.media_url} onChange={(url) => setF({ ...f, media_url: url })} />
        <button onClick={create} data-testid="create-testad-btn" className="px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold">Create & Activate Test Ad</button>
      </div>
      <div className="space-y-2">
        {ads.map((a) => (
          <div key={a.id} className="flex items-center gap-3 p-3 rounded-xl border border-border surface-2">
            {a.media_url && (a.ad_type === "video" ? <video src={a.media_url} className="h-12 w-16 object-cover rounded" /> : <img src={a.media_url} alt="" className="h-12 w-16 object-cover rounded" />)}
            <div className="flex-1 min-w-0"><p className="font-medium truncate">{a.title}</p><p className="text-xs text-muted-foreground">{a.brand_name} · {a.ad_type} · <span className={a.status === "active" ? "text-primary" : ""}>{a.status}</span></p></div>
            <button onClick={() => toggle(a)} className="text-xs px-3 py-1 rounded border border-border">{a.status === "active" ? "Pause" : "Activate"}</button>
            <button onClick={() => del(a)} className="p-2 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {!ads.length && <p className="text-muted-foreground text-center py-6">No test ads yet.</p>}
      </div>
    </div>
  );
}

function Campaigns() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/campaigns").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const act = async (id, action) => { let reason; if (action === "reject") { reason = window.prompt("Rejection reason (optional):") || ""; } await api.patch(`/admin/campaigns/${id}`, { action, reason }); toast.success("Updated"); load(); };
  return (
    <div className="space-y-2">
      {rows.map((c) => (
        <div key={c.id} className="flex items-center gap-3 p-3 rounded-xl border border-border surface-2" data-testid={`admin-campaign-${c.id}`}>
          {c.media_url && (c.ad_type === "video" ? <video src={c.media_url} className="h-14 w-20 object-cover rounded" /> : <img src={c.media_url} alt="" className="h-14 w-20 object-cover rounded" />)}
          <div className="flex-1 min-w-0"><p className="font-medium truncate">{c.title}</p><p className="text-xs text-muted-foreground">{c.advertiser_company} · {c.ad_type} · <span className="capitalize">{c.status}</span></p></div>
          <div className="flex gap-1 flex-wrap">
            {c.status === "pending" && <button onClick={() => act(c.id, "approve")} className="text-xs px-2 py-1 rounded border border-primary text-primary">Approve</button>}
            {(c.status === "approved" || c.status === "paused") && <button onClick={() => act(c.id, "activate")} className="text-xs px-2 py-1 rounded border border-primary text-primary">Activate</button>}
            {c.status === "active" && <button onClick={() => act(c.id, "pause")} className="text-xs px-2 py-1 rounded border border-border">Pause</button>}
            {c.status !== "rejected" && <button onClick={() => act(c.id, "reject")} className="text-xs px-2 py-1 rounded border border-border text-destructive">Reject</button>}
            <button onClick={() => act(c.id, "delete")} className="text-xs px-2 py-1 rounded border border-border">Delete</button>
          </div>
        </div>
      ))}
      {!rows.length && <p className="text-muted-foreground text-center py-10">No advertiser campaigns yet.</p>}
    </div>
  );
}

function AdvertisersList() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/advertisers").then((r) => setRows(r.data)); }, []);
  return (
    <div className="space-y-2">
      {rows.map((a) => (
        <div key={a.id} className="flex items-center gap-3 p-3 rounded-xl border border-border surface-2">
          <div className="flex-1"><p className="font-medium">{a.company || a.name}</p><p className="text-xs text-muted-foreground">{a.contact} · {a.email} · {a.phone}</p></div>
          <span className="text-xs text-muted-foreground">{a.campaign_count} campaigns</span>
        </div>
      ))}
      {!rows.length && <p className="text-muted-foreground text-center py-10">No advertisers registered yet. Share /advertiser/register.</p>}
    </div>
  );
}

function AdsPanel() {
  const [sub, setSub] = useState("Settings");
  const [ads, setAds] = useState(null);
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/ads").then((r) => setAds(r.data)); api.get("/admin/ad-analytics").then((r) => setStats(r.data)); }, []);
  const auto = useAutosave(async (d) => {
    await api.put("/admin/ads", { enabled: d.enabled, interval: parseInt(d.interval) || 5, countdown: parseInt(d.countdown) || 10, provider: d.provider, ad_source: d.ad_source, placement: d.placement, daily_limit: parseInt(d.daily_limit) || 30, premium_bypass: d.premium_bypass, test_mode: d.test_mode, fallback_message: d.fallback_message });
  });
  const upd = (patch) => { const n = { ...ads, ...patch }; setAds(n); auto.trigger(n); };
  if (!ads) return null;
  const SUBS = ["Settings", "Test Ads", "Campaigns", "Advertisers", "Analytics"];
  return (
    <div>
      <div className="flex gap-2 flex-wrap mb-6">
        {SUBS.map((s) => <button key={s} onClick={() => setSub(s)} data-testid={`ads-sub-${s.toLowerCase().replace(/ /g, "-")}`} className={`px-4 py-2 rounded-full text-sm ${sub === s ? "bg-primary text-primary-foreground" : "border border-border"}`}>{s}</button>)}
      </div>

      {sub === "Settings" && (
        <div className="space-y-6">
          {stats && <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[["Campaigns", stats.campaigns_total], ["Active", stats.active], ["Pending", stats.pending], ["Impressions", stats.impressions]].map(([l, v]) => <div key={l} className="rounded-2xl border border-border surface-2 p-4"><p className="track-label text-xs text-muted-foreground">{l}</p><p className="text-2xl font-display font-bold mt-1">{v}</p></div>)}
          </div>}
          <section className="rounded-2xl border border-border surface-2 p-6 space-y-6">
            <div className="flex items-center justify-between"><h3 className="text-lg font-display font-bold flex items-center gap-3">Ad System <SaveBadge status={auto.status} /></h3>
              <label className="flex items-center gap-2 font-medium"><input type="checkbox" checked={!!ads.enabled} onChange={(e) => upd({ enabled: e.target.checked })} data-testid="ads-enabled" /> {ads.enabled ? "Enabled" : "Disabled"}</label></div>
            <div className="grid md:grid-cols-2 gap-6">
              <div><p className="track-label text-xs text-muted-foreground mb-2">Ad Source</p>
                <select value={ads.ad_source || "test"} onChange={(e) => upd({ ad_source: e.target.value })} data-testid="ads-source" className="w-full bg-background border border-border rounded-xl px-4 py-2.5">
                  <option value="test">Test Ads (admin uploads)</option><option value="direct">Direct Advertiser Campaigns</option><option value="external">External Ad Network (future)</option>
                </select></div>
              <div><p className="track-label text-xs text-muted-foreground mb-2">External Provider (when source = external)</p>
                <select value={ads.provider || "simulated"} onChange={(e) => upd({ provider: e.target.value })} className="w-full bg-background border border-border rounded-xl px-4 py-2.5">
                  <option value="simulated">Simulated</option><option value="google-admob">Google AdMob</option><option value="unity">Unity Ads</option><option value="applovin">AppLovin</option>
                </select></div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><p className="text-xs text-muted-foreground mb-1">Free chapters before ads</p><Field type="number" value={ads.interval ?? 5} onChange={(e) => upd({ interval: e.target.value })} data-testid="ads-interval" /></div>
              <div><p className="text-xs text-muted-foreground mb-1">Ad length (s)</p><Field type="number" value={ads.countdown ?? 5} onChange={(e) => upd({ countdown: e.target.value })} /></div>
              <div><p className="text-xs text-muted-foreground mb-1">Daily ad limit per reader</p><Field type="number" value={ads.daily_limit ?? 30} onChange={(e) => upd({ daily_limit: e.target.value })} data-testid="ads-daily-limit" /></div>
              <div className="flex flex-col justify-end gap-2">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={ads.premium_bypass !== false} onChange={(e) => upd({ premium_bypass: e.target.checked })} /> Premium bypass</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={ads.test_mode !== false} onChange={(e) => upd({ test_mode: e.target.checked })} data-testid="ads-test-mode" /> Test mode</label>
              </div>
            </div>
            <div><p className="track-label text-xs text-muted-foreground mb-2">Fallback message (ad fails to load)</p><Field value={ads.fallback_message || ""} onChange={(e) => upd({ fallback_message: e.target.value })} /></div>
            <div className="rounded-xl border border-border p-4 bg-background text-sm text-muted-foreground">Chapters 1–{ads.interval ?? 5} free · every chapter after needs a rewarded ad · max {ads.daily_limit ?? 30} ads/reader/day (auto-resets daily) · Premium {ads.premium_bypass !== false ? "ad-free" : "sees ads"}.</div>
          </section>
        </div>
      )}
      {sub === "Test Ads" && <TestAds />}
      {sub === "Campaigns" && <Campaigns />}
      {sub === "Advertisers" && <AdvertisersList />}
      {sub === "Analytics" && stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[["Total Impressions", stats.impressions], ["Completed Views", stats.completed_views], ["Clicks", stats.clicks], ["CTR", stats.ctr + "%"], ["Active Campaigns", stats.active], ["Test Ads", stats.test_ads]].map(([l, v]) => <div key={l} className="rounded-2xl border border-border surface-2 p-5"><p className="track-label text-xs text-muted-foreground">{l}</p><p className="text-3xl font-display font-bold mt-1">{v}</p></div>)}
        </div>
      )}
    </div>
  );
}

/* ---------- SETTINGS ---------- */
function Settings() {
  const [hp, setHp] = useState(null);
  const [rewards, setRewards] = useState([]);
  useEffect(() => {
    api.get("/admin/homepage").then((r) => setHp(r.data || {}));
    api.get("/admin/rewards").then((r) => setRewards(r.data));
  }, []);
  const hpAuto = useAutosave(async (data) => { await api.put("/admin/homepage", data); });
  const updHp = (patch) => { const n = { ...hp, ...patch }; setHp(n); hpAuto.trigger(n); };
  const rewardTimers = useRef({});
  const [rewardSaved, setRewardSaved] = useState({});
  const updReward = (i, patch) => {
    const arr = [...rewards]; arr[i] = { ...arr[i], ...patch }; setRewards(arr);
    const r = arr[i];
    clearTimeout(rewardTimers.current[r.id]);
    rewardTimers.current[r.id] = setTimeout(async () => {
      try { await api.put(`/admin/rewards/${r.id}`, r); setRewardSaved((s) => ({ ...s, [r.id]: true })); setTimeout(() => setRewardSaved((s) => ({ ...s, [r.id]: false })), 2000); }
      catch (e) { toast.error(apiError(e)); }
    }, 900);
  };
  if (!hp) return null;

  return (
    <div className="space-y-8">
      <p className="text-sm text-muted-foreground">Settings below <span className="text-primary">save automatically</span> as you type.</p>
      <section className="rounded-2xl border border-border surface-2 p-6">
        <h3 className="text-lg font-display font-bold mb-4 flex items-center gap-3">Homepage Hero <SaveBadge status={hpAuto.status} /></h3>
        <div className="space-y-3">
          <Field value={hp.label || ""} onChange={(e) => updHp({ label: e.target.value })} placeholder="Small label" data-testid="hp-label" />
          <div className="flex gap-3"><Field value={hp.headline || ""} onChange={(e) => updHp({ headline: e.target.value })} placeholder="Headline" /><Field value={hp.highlight || ""} onChange={(e) => updHp({ highlight: e.target.value })} placeholder="Highlighted word" className="w-48" /></div>
          <Area rows={2} value={hp.description || ""} onChange={(e) => updHp({ description: e.target.value })} placeholder="Description" />
          <ImageUpload label="Hero image" value={hp.hero_image} onChange={(url) => updHp({ hero_image: url })} testid="hp-hero-upload" />
        </div>
      </section>

      <section className="rounded-2xl border border-border surface-2 p-6">
        <h3 className="text-lg font-display font-bold mb-4">Streak Rewards <span className="text-xs text-muted-foreground font-normal">(auto-saved)</span></h3>
        <div className="space-y-2">
          {rewards.map((r, i) => (
            <div key={r.id} className="flex flex-wrap items-center gap-2 p-2 rounded-lg border border-border">
              <Field type="number" value={r.days} onChange={(e) => updReward(i, { days: parseInt(e.target.value) || 0 })} className="w-20" />
              <Field value={r.title} onChange={(e) => updReward(i, { title: e.target.value })} className="w-40" />
              <Field value={r.reward} onChange={(e) => updReward(i, { reward: e.target.value })} className="flex-1 min-w-[140px]" />
              <Field value={r.badge} onChange={(e) => updReward(i, { badge: e.target.value })} className="w-16" />
              <label className="flex items-center gap-1 text-xs"><span>Prem days</span><Field type="number" value={r.premium_days} onChange={(e) => updReward(i, { premium_days: parseInt(e.target.value) || 0 })} className="w-16" /></label>
              {rewardSaved[r.id] && <span className="text-xs text-primary inline-flex items-center gap-1"><Check className="h-3.5 w-3.5" />Saved</span>}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

/* ---------- ROOT ---------- */
export default function Admin() {
  const { user } = useApp();
  const nav = useNavigate();
  const [tab, setTab] = useState("Stats");
  const [authors, setAuthors] = useState([]);
  const [genres, setGenres] = useState([]);
  const [tropes, setTropes] = useState([]);
  const [series, setSeries] = useState([]);
  const [contentTab, setContentTab] = useState("Genres");

  const loadRefs = useCallback(() => {
    api.get("/authors").then((r) => setAuthors(r.data));
    api.get("/genres?all=true").then((r) => setGenres(r.data));
    api.get("/tropes?all=true").then((r) => setTropes(r.data));
    api.get("/admin/series").then((r) => setSeries(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user === null) { nav("/login"); return; }
    if (user && user.role !== "admin") { nav("/"); return; }
    if (user) loadRefs();
  }, [user, loadRefs]); // eslint-disable-line

  if (!user || user.role !== "admin") return <div className="py-24 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-10">
      <h1 className="text-4xl md:text-5xl font-display font-bold">Admin CMS</h1>
      <div className="flex gap-2 flex-wrap mt-6 mb-8">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.toLowerCase().replace(/ /g, "-")}`}
            className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${tab === t ? "bg-primary text-primary-foreground" : "border border-border hover:border-primary"}`}>{t}</button>
        ))}
      </div>

      {tab === "Stats" && <Stats />}
      {tab === "Books" && <Books authors={authors} genres={genres} tropes={tropes} series={series} />}
      {tab === "Publish Book" && <PublishBook authors={authors} genres={genres} tropes={tropes} series={series} reload={loadRefs} />}
      {tab === "Authors" && <CrudSection title="Authors" endpoint="/admin/authors" listEndpoint="/authors" reload={loadRefs}
        fields={[{ k: "name", label: "Name" }, { k: "pen_name", label: "Pen name" }, { k: "bio", label: "Bio", type: "area" }, { k: "avatar", label: "Avatar image", type: "image" }]} />}
      {tab === "Content" && (
        <div>
          <div className="flex gap-2 mb-6">
            {["Genres", "Tropes", "Series"].map((c) => <button key={c} onClick={() => setContentTab(c)} className={`px-4 py-2 rounded-full text-sm ${contentTab === c ? "bg-primary text-primary-foreground" : "border border-border"}`}>{c}</button>)}
          </div>
          {contentTab === "Genres" && <CrudSection title="Genres" endpoint="/admin/genres" listEndpoint="/genres?all=true" reload={loadRefs}
            fields={[{ k: "name", label: "Name" }, { k: "description", label: "Description", type: "area" }, { k: "image", label: "Genre image", type: "image" }, { k: "order", label: "Order" }]} />}
          {contentTab === "Tropes" && <CrudSection title="Tropes" endpoint="/admin/tropes" listEndpoint="/tropes?all=true" reload={loadRefs}
            fields={[{ k: "name", label: "Name" }, { k: "description", label: "Description", type: "area" }, { k: "image", label: "Trope image", type: "image" }, { k: "order", label: "Order" }]} />}
          {contentTab === "Series" && <CrudSection title="Series" endpoint="/admin/series" listEndpoint="/admin/series" reload={loadRefs} extra={{ authors }}
            fields={[{ k: "name", label: "Name" }, { k: "description", label: "Description", type: "area" }, { k: "cover", label: "Series cover", type: "image" }, { k: "author_id", label: "Author", type: "select", opts: "authors" }]} />}
        </div>
      )}
      {tab === "Premium" && <PremiumPlans />}
      {tab === "Payments" && <PaymentsPanel />}
      {tab === "Users" && <UsersTab />}
      {tab === "Ads" && <AdCMS />}
      {tab === "Header & Footer" && <SiteLayoutCMS />}
      {tab === "SEO" && <SeoCMS />}
      {tab === "Tracking & Verification" && <TrackingCMS />}
      {tab === "Settings" && <Settings />}
    </div>
  );
}
